
import React, { createContext, useContext, useState, useEffect, useLayoutEffect, ReactNode } from 'react';
import { Language, translations } from '../translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: typeof translations.en;
}

declare global {
  interface Window {
    /** Set by the language bootstrap script in index.html, before React boots. */
    __MM_LANG__?: Language;
  }
}

const STORAGE_KEY = 'mm-language';

const isLanguage = (value: unknown): value is Language => value === 'en' || value === 'nl';

/**
 * Which language a visitor starts in:
 *
 *   1. their own earlier choice, from localStorage
 *   2. what their browser asks for — Dutch gets Dutch
 *   3. English
 *
 * The same rules run in the bootstrap script in index.html so that
 * <html lang> is right from the first paint. That script leaves its result
 * on window.__MM_LANG__ and we reuse it here rather than deciding twice;
 * the rest of this function is the fallback for when it did not run.
 */
export const detectLanguage = (): Language => {
  if (typeof window === 'undefined') return 'en'; // SSR/prerender

  if (isLanguage(window.__MM_LANG__)) return window.__MM_LANG__;

  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLanguage(saved)) return saved;
  } catch {
    // Storage throws in some privacy modes — fall through to the browser preference.
  }

  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of preferred) {
    const lower = String(tag ?? '').toLowerCase();
    if (lower === 'nl' || lower.startsWith('nl-')) return 'nl';
    if (lower === 'en' || lower.startsWith('en-')) return 'en';
  }

  return 'en';
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// useLayoutEffect on the client (applies before paint, so the language swap is
// invisible), a no-op useEffect on the server so prerender does not warn.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Start in the prerender language (English) so the first client render matches
  // the server HTML and hydrateRoot adopts it cleanly. Right after mount we
  // switch to the visitor's real preference; the layout effect applies it before
  // the browser paints, so a Dutch visitor never sees a flash of English.
  const [language, setLanguageState] = useState<Language>('en');

  useIsomorphicLayoutEffect(() => {
    const preferred = detectLanguage();
    setLanguageState((prev) => (preferred === prev ? prev : preferred));
    document.documentElement.lang = preferred;
  }, []);

  const handleSetLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof window === 'undefined') return;
    window.__MM_LANG__ = lang;
    document.documentElement.lang = lang;
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // Nothing to do — the choice just will not survive a reload.
    }
  };

  const value = {
    language,
    setLanguage: handleSetLanguage,
    t: translations[language],
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
